import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  collection,
  Firestore,
  Transaction,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Match, MatchType, MMR_CONSTANTS } from "@/types/match";

export class MmrService {
  private static instance: MmrService;

  private constructor() {}

  static getInstance(): MmrService {
    if (!MmrService.instance) {
      MmrService.instance = new MmrService();
    }
    return MmrService.instance;
  }

  /**
   * Calculate MMR change based on match result
   */
  calculateMmrChange(
    currentMmr: number,
    isWin: boolean,
    matchType: MatchType,
    opponentMmr?: number
  ): number {
    // Base K-factor
    const K = MMR_CONSTANTS.BASE_K_FACTOR;

    // Multiplier based on match type
    const matchMultiplier =
      MMR_CONSTANTS.MATCH_TYPE_MULTIPLIERS[matchType] || 0.5;

    let mmrChange = 0;

    if ((matchType as string) === "solo") {
      // For solo rounds, we give a small MMR boost based on participation
      mmrChange = 5; // Small fixed gain for playing
    } else if (opponentMmr) {
      // For matches against other players

      // Calculate expected outcome using ELO formula
      const expectedOutcome =
        1 / (1 + Math.pow(10, (opponentMmr - currentMmr) / 400));

      // Actual outcome (1 for win, 0 for loss)
      const actualOutcome = isWin ? 1 : 0;

      // Calculate MMR change
      mmrChange = Math.round(
        K * matchMultiplier * (actualOutcome - expectedOutcome)
      );
    } else {
      // Default change if no opponent MMR is provided
      mmrChange = isWin ? 10 * matchMultiplier : -5 * matchMultiplier;
    }

    // Ensure MMR stays within bounds
    const newMmr = currentMmr + mmrChange;
    if (newMmr < MMR_CONSTANTS.MIN_MMR) {
      mmrChange = MMR_CONSTANTS.MIN_MMR - currentMmr;
    } else if (newMmr > MMR_CONSTANTS.MAX_MMR) {
      mmrChange = MMR_CONSTANTS.MAX_MMR - currentMmr;
    }

    return Math.round(mmrChange);
  }

  /**
   * Get the skill tier based on MMR
   */
  getSkillTier(mmr: number): string {
    const thresholds = MMR_CONSTANTS.RANKING_THRESHOLDS;

    if (mmr >= thresholds.expert.min) {
      return "expert";
    } else if (mmr >= thresholds.advanced.min) {
      return "advanced";
    } else if (mmr >= thresholds.intermediate.min) {
      return "intermediate";
    } else {
      return "beginner";
    }
  }

  /**
   * Update player's MMR after a match
   */
  async updatePlayerMmr(
    userId: string,
    match: Match,
    transaction?: Transaction
  ): Promise<number> {
    if (!db) throw new Error("Firestore instance not initialized");

    // Reference to player's profile or stats document
    const playerStatsRef = doc(
      collection(db as Firestore, "playerStats"),
      userId
    );

    try {
      // Get current player stats
      let currentMmr = MMR_CONSTANTS.INITIAL_MMR;
      let playerStats: any = {};

      const playerDoc = await getDoc(playerStatsRef);

      if (playerDoc.exists()) {
        playerStats = playerDoc.data();
        currentMmr = playerStats.mmr || MMR_CONSTANTS.INITIAL_MMR;
      }

      // Calculate MMR change
      const mmrChange = this.calculateMmrChange(
        currentMmr,
        (match as any).isWin,
        (match as any).matchType,
        (match as any).opponentId ? await this.getPlayerMmr((match as any).opponentId) : undefined
      );

      // Update player stats
      const newMmr = currentMmr + mmrChange;
      const skillTier = this.getSkillTier(newMmr);

      const statsUpdate = {
        mmr: newMmr,
        skillTier,
        totalMatches: increment(1),
        ...(match.isWin ? { wins: increment(1) } : { losses: increment(1) }),
        lastMatchDate: match.date,
        lastUpdated: new Date().toISOString(),
      };

      // Either use transaction or direct update
      if (transaction) {
        if (playerDoc.exists()) {
          transaction.update(playerStatsRef, statsUpdate);
        } else {
          transaction.set(playerStatsRef, {
            userId,
            mmr: newMmr,
            skillTier,
            totalMatches: 1,
            wins: match.isWin ? 1 : 0,
            losses: match.isWin ? 0 : 1,
            lastMatchDate: match.date,
            lastUpdated: new Date().toISOString(),
          });
        }
      } else {
        if (playerDoc.exists()) {
          await updateDoc(playerStatsRef, statsUpdate);
        } else {
          await setDoc(playerStatsRef, {
            userId,
            mmr: newMmr,
            skillTier,
            totalMatches: 1,
            wins: match.isWin ? 1 : 0,
            losses: match.isWin ? 0 : 1,
            lastMatchDate: match.date,
            lastUpdated: new Date().toISOString(),
          });
        }
      }

      return mmrChange;
    } catch (error) {
      console.error("Error updating player MMR:", error);
      throw error;
    }
  }

  /**
   * Get a player's current MMR
   */
  async getPlayerMmr(userId: string): Promise<number> {
    if (!db) throw new Error("Firestore instance not initialized");

    try {
      const playerStatsRef = doc(
        collection(db as Firestore, "playerStats"),
        userId
      );
      const playerDoc = await getDoc(playerStatsRef);

      if (playerDoc.exists()) {
        const playerData = playerDoc.data();
        return playerData.mmr || MMR_CONSTANTS.INITIAL_MMR;
      }

      return MMR_CONSTANTS.INITIAL_MMR;
    } catch (error) {
      console.error("Error getting player MMR:", error);
      return MMR_CONSTANTS.INITIAL_MMR;
    }
  }
}
